
import React, { useState } from 'react';
import { GenerationForm } from '@/components/GenerationForm';
import { ResultsTable } from '@/components/ResultsTable';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { LogOut, UserCog } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { CreditDisplay } from '@/components/Dashboard/CreditDisplay';
import { Link } from 'react-router-dom';
import LoadingOverlay from '@/components/LoadingOverlay';

interface GeneratedContent {
  title: string;
  description: string;
  altText: string;
  metaDescription: string;
  imageName: string;
}

const Index = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [results, setResults] = useState<GeneratedContent[]>([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const handleGenerate = async (generatedResults: GeneratedContent[]) => {
    console.log('handleGenerate called with:', generatedResults.length, 'results');
    
    // If empty results array, this is the initial call to show loading state
    if (generatedResults.length === 0) {
      console.log('Initial call to show loading state');
      setIsGenerating(true);
      return;
    }

    try {
      if (generatedResults.length > 0) {
        console.log('Processing generation results...');
        
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { data: credits, error: creditCheckError } = await supabase
          .from('user_credits')
          .select('credits')
          .eq('user_id', user.id)
          .single();

        if (creditCheckError) throw new Error('CREDIT_CHECK_ERROR');

        if (!credits || credits.credits < generatedResults.length) {
          throw new Error('INSUFFICIENT_CREDITS');
        }

        const { error: creditError } = await supabase
          .rpc('decrement_credits', { amount: generatedResults.length });

        if (creditError) throw new Error('CREDIT_ERROR');

        setResults(generatedResults);
        toast.success('Content generated successfully!');
      } else {
        toast.info('Could not generate content for the selected images.');
      }
    } catch (error: any) {
      console.error('Generation error:', error);
      switch (error.message) {
        case 'User not authenticated':
          toast.error('Please log in to generate content.');
          break;
        case 'INSUFFICIENT_CREDITS':
          toast.error('Not enough credits. Please upgrade your plan.');
          break;
        case 'CREDIT_ERROR':
          toast.error('Error processing credits. Please try again.');
          break;
        case 'CREDIT_CHECK_ERROR':
          toast.error('Unable to verify credit balance. Please try again.');
          break;
        default:
          toast.error('An unexpected error occurred. Please try again.');
      }
    } finally {
      console.log('Stopping loading state');
      setIsGenerating(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const handleProgressUpdate = (newProgress: { current: number; total: number }) => {
    console.log('Progress updated:', newProgress);
    if (newProgress.total > 0 && !isGenerating) {
      setIsGenerating(true);
    }
    setProgress(newProgress);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success('Logged out successfully');
  };

  const handleDownloadCsv = () => {
    if (results.length === 0) return;

    const headers = ['Image Name', 'Title', 'Description', 'Alt Text', 'Meta Description'];
    const csvContent = [
      headers.join(','),
      ...results.map(result => [
        result.imageName,
        `"${result.title}"`,
        `"${result.description}"`,
        `"${result.altText}"`,
        `"${result.metaDescription}"`,
      ].join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `generation-${new Date().toISOString()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  console.log('Rendering Index with isGenerating:', isGenerating, 'progress:', progress);
  
  // Ensure we have valid progress values
  const validProgress = {
    current: progress.current || 0,
    total: progress.total || 1
  };
  
  console.log('Using progress values for LoadingOverlay:', validProgress);

  return (
    <>
      <LoadingOverlay 
        visible={isGenerating} 
        progress={validProgress}
        onClose={() => {
          setIsGenerating(false);
          setProgress({ current: 0, total: 0 });
        }}
      />
      
      <div className="min-h-screen bg-gradient-to-b from-white to-sage-50/30">
        <div className="container py-12 space-y-8">
          {/* Header */}
          <header className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-sage-800">Dashboard</h1>
            <div className="flex gap-2">
              <Link to="/app/account">
                <Button
                  variant="outline"
                  className="gap-2"
                >
                  <UserCog className="w-4 h-4" />
                  Your Account
                </Button>
              </Link>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </header>

          {/* Credit Display */}
          <CreditDisplay />

          {/* Generator Section */}
          <section className="space-y-8">
            <div className="glass-card rounded-xl p-6 shadow-xl">
              <GenerationForm
                onGenerate={handleGenerate}
                isGenerating={isGenerating}
                onProgressUpdate={handleProgressUpdate}
              />
            </div>

            {/* Results Section */}
            <div className="glass-card rounded-xl p-6 shadow-xl">
              <ResultsTable
                results={results}
                onDownload={handleDownloadCsv}
                isGenerating={isGenerating}
              />
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Index;
